/**
 * Error Overlay Component
 *
 * Full-screen error overlay displayed when critical API endpoints fail.
 * Instagram/Discord style with error-specific icons, messages, and retry functionality.
 */

import React, { useEffect } from 'react';
import { WifiOff, ServerCrash, ShieldAlert, AlertTriangle, RefreshCw } from 'lucide-react';
import { useCriticalError } from '../../contexts/CriticalErrorContext';
import { ErrorType } from '../../utils/errorClassification';

/**
 * ErrorOverlay Component
 *
 * Renders a full-screen blocking overlay when critical API failures occur.
 * Features:
 * - Error-specific icons and color schemes
 * - User-friendly error messages
 * - Retry button to re-attempt failed request
 * - Retry count display
 * - Accessibility features (ARIA attributes, keyboard navigation, focus management)
 */
const ErrorOverlay: React.FC = () => {
  const { state, retry } = useCriticalError();

  // Prevent body scroll when overlay is visible
  useEffect(() => {
    if (state.isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [state.isVisible]);

  // Don't render if not visible
  if (!state.isVisible) {
    return null;
  }

  /**
   * Get error-specific icon
   */
  const Icon = (() => {
    switch (state.errorType) {
      case ErrorType.NETWORK_ERROR:
        return WifiOff;
      case ErrorType.SERVER_ERROR:
        return ServerCrash;
      case ErrorType.AUTH_ERROR:
        return ShieldAlert;
      default:
        return AlertTriangle;
    }
  })();

  /**
   * Get error-specific color scheme
   */
  const colorScheme = (() => {
    switch (state.errorType) {
      case ErrorType.NETWORK_ERROR:
        return 'from-blue-600 to-blue-800';
      case ErrorType.SERVER_ERROR:
        return 'from-red-600 to-red-800';
      case ErrorType.AUTH_ERROR:
        return 'from-yellow-600 to-yellow-800';
      default:
        return 'from-gray-600 to-gray-800';
    }
  })();

  /**
   * Get error-specific title
   */
  const errorTitle = (() => {
    // Show different title when auto-reconnecting
    if (state.isAutoRetrying && state.errorType === ErrorType.NETWORK_ERROR) {
      return 'Reconectando...';
    }

    switch (state.errorType) {
      case ErrorType.NETWORK_ERROR:
        return 'Sem Conexão';
      case ErrorType.SERVER_ERROR:
        return 'Serviço Indisponível';
      case ErrorType.AUTH_ERROR:
        return 'Sessão Expirada';
      default:
        return 'Erro Inesperado';
    }
  })();

  /**
   * Handle retry button click
   */
  const handleRetry = () => {
    retry();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-sm electron-no-drag"
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-overlay-title"
      aria-describedby="error-overlay-message"
    >
      <div className="flex flex-col items-center justify-center max-w-md mx-auto px-6 text-center">
        {/* Icon with gradient background */}
        <div
          className={`mb-6 p-6 rounded-full bg-gradient-to-br ${colorScheme} shadow-2xl`}
          aria-hidden="true"
        >
          <Icon className="w-16 h-16 text-white" strokeWidth={1.5} />
        </div>

        {/* Title */}
        <h2
          id="error-overlay-title"
          className="text-2xl font-bold text-white mb-3"
        >
          {errorTitle}
        </h2>

        {/* Message */}
        <p
          id="error-overlay-message"
          className="text-gray-300 mb-8 leading-relaxed"
        >
          {state.message}
        </p>

        {/* Auto-Reconnecting Indicator */}
        {state.isAutoRetrying && (
          <div className="mb-6 flex items-center gap-3 text-blue-400">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span className="font-medium">
              {state.nextRetryIn !== null
                ? `Tentando novamente em ${state.nextRetryIn}s...`
                : 'Verificando conexão...'
              }
            </span>
          </div>
        )}

        {/* Retry Button */}
        <button
          onClick={handleRetry}
          disabled={state.isAutoRetrying}
          className="
            flex items-center gap-3 px-8 py-4 rounded-lg
            bg-white text-gray-900 font-semibold
            transition-all duration-200
            shadow-lg
            focus:outline-none focus:ring-4 focus:ring-white/50
            disabled:opacity-60 disabled:cursor-not-allowed
            enabled:hover:bg-gray-100 enabled:active:bg-gray-200 enabled:hover:shadow-xl
          "
          aria-label={state.isAutoRetrying ? 'Conectando...' : 'Tentar novamente'}
        >
          <RefreshCw className={`w-5 h-5 ${state.isAutoRetrying ? 'animate-spin' : ''}`} />
          {state.isAutoRetrying ? 'Conectando...' : 'Tentar Novamente'}
        </button>

        {/* Retry count and help text */}
        <div className="mt-4 space-y-1">
          {state.retryCount > 0 && (
            <p className="text-gray-500 text-sm">
              Tentativas manuais: {state.retryCount}
            </p>
          )}
          {state.autoRetryAttempt > 0 && (
            <p className="text-gray-500 text-sm">
              Tentativa automática: {state.autoRetryAttempt}
            </p>
          )}
          {state.retryCount >= 5 && (
            <p className="text-yellow-400 text-sm">
              Se o problema persistir, entre em contato com o suporte.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorOverlay;
